import type { MarketConfiguration } from "@/config/markets/const";
import type {
  CustomProduct,
  LandingPageBody,
  LandingPageParams,
} from "@apptus/esales-api";
import type { Loop54Content } from "@server/utils/parseLoop/types";
import type { Variant } from "types";
import getRawLandingPage from "./getRawLandingPage";
import mapElevateFacets from "./mapElevateFacets";
import { mapElevateProduct } from "./mapElevateProducts";
import { mapElevateVariant } from "./mapElevateVariants";

export default async function getLandingPage(
  market: MarketConfiguration,
  input: LandingPageParams,
  config?: LandingPageBody,
  allowDefault = true,
): Promise<Loop54Content & { lists: Record<string, Variant[]> }> {
  const response = await getRawLandingPage(market, input, config);
  const lists = Object.fromEntries(
    response.recommendationLists.map((list) => [
      list.id,
      list.productGroups.map((productGroup) =>
        mapElevateVariant({
          product: productGroup.products[0] as CustomProduct,
          marketConfig: market,
        }),
      ),
    ]),
  );

  if (!response.primaryList) {
    return {
      items: [],
      filter: [],
      count: 0,
      lists,
    };
  }

  const { productGroups, facets, totalHits: count } = response.primaryList;
  const items = productGroups.map((productGroup) =>
    mapElevateProduct({
      product: productGroup.products[0] as CustomProduct,
      marketConfig: market,
      config: {
        allowDefault,
        hasVariants: productGroup.products.length > 1,
      },
    }),
  );
  const filter = mapElevateFacets(facets);

  return { items, filter, count, lists };
}
